import { z } from 'zod';
import { API_BASE_URL, API_PREFIX } from '../config';

const shareSchema = z.object({
  id: z.string(),
  fileId: z.string(),
  code: z.string(),
  password: z.string().nullable(),
  expiresAt: z.string().nullable(),
  downloadCount: z.number(),
  isDirectLink: z.boolean(),
});

const fileSchema = z.object({
  id: z.string(),
  name: z.string(),
  path: z.string(),
  r2Key: z.string(),
  size: z.number(),
  mimeType: z.string().nullable(),
  isFolder: z.boolean(),
  updatedAt: z.string(),
});

const shareDetailSchema = shareSchema.extend({ file: fileSchema });

export type FileItem = z.infer<typeof fileSchema>;
export type ShareItem = z.infer<typeof shareSchema>;
export type ShareDetail = z.infer<typeof shareDetailSchema>;

function authHeaders(token: string | null) {
  const headers: Record<string, string> = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

async function handleResponse<T>(response: Response, schema: z.ZodType<T>): Promise<T> {
  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || '请求失败');
  }
  const json = await response.json();
  return schema.parse(json);
}

export const apiClient = {
  async listFiles(token: string | null): Promise<FileItem[]> {
    const response = await fetch(`${API_BASE_URL}${API_PREFIX}/files`, {
      headers: {
        ...authHeaders(token),
      },
    });

    return handleResponse(response, z.array(fileSchema));
  },

  async deleteFile(token: string | null, id: string) {
    const response = await fetch(`${API_BASE_URL}/api/files/${id}`, {
      method: 'DELETE',
      headers: authHeaders(token),
    });

    if (!response.ok) {
      throw new Error(await response.text());
    }
  },

  async createShare(
    token: string | null,
    body: { fileId: string; password?: string; expiresAt?: string; isDirectLink?: boolean },
  ): Promise<ShareItem> {
    const response = await fetch(`${API_BASE_URL}/api/share`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders(token),
      },
      body: JSON.stringify(body),
    });

    return handleResponse(response, shareSchema);
  },

  async getShare(code: string): Promise<ShareDetail> {
    const response = await fetch(`${API_BASE_URL}/api/share/${code}`);
    return handleResponse(response, shareDetailSchema);
  },

  // 分片上传相关 API
  async initUpload(
    token: string | null,
    body: { fileName: string; fileSize: number; mimeType?: string },
  ) {
    const response = await fetch(`${API_BASE_URL}/api/upload/init`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders(token),
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(await response.text());
    }
    return response.json() as Promise<{
      uploadId: string;
      chunkSize: number;
      totalChunks: number;
    }>;
  },

  async uploadChunk(
    token: string | null,
    body: { uploadId: string; chunkIndex: number; chunkData: string },
  ) {
    const response = await fetch(`${API_BASE_URL}/api/upload/chunk`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders(token),
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(await response.text());
    }
    return response.json() as Promise<{
      chunkIndex: number;
      uploadedChunks: number[];
      progress: number;
    }>;
  },

  async getUploadStatus(token: string | null, uploadId: string) {
    const response = await fetch(`${API_BASE_URL}/api/upload/status/${uploadId}`, {
      headers: authHeaders(token),
    });

    if (!response.ok) {
      throw new Error(await response.text());
    }
    return response.json() as Promise<{
      uploadId: string;
      fileName: string;
      fileSize: number;
      totalChunks: number;
      uploadedChunks: number[];
      progress: number;
      status: string;
    }>;
  },

  async completeUpload(
    token: string | null,
    body: { uploadId: string },
  ): Promise<FileItem> {
    const response = await fetch(`${API_BASE_URL}/api/upload/complete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders(token),
      },
      body: JSON.stringify(body),
    });

    return handleResponse(response, fileSchema);
  },

  // 设置相关 API
  async getSettings(token: string | null) {
    const response = await fetch(`${API_BASE_URL}/api/settings`, {
      headers: authHeaders(token),
    });

    if (!response.ok) {
      throw new Error(await response.text());
    }
    return response.json() as Promise<Record<string, string>>;
  },

  async updateSetting(
    token: string | null,
    body: { key: string; value: string },
  ) {
    const response = await fetch(`${API_BASE_URL}/api/settings`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders(token),
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(await response.text());
    }
    return response.json() as Promise<{ ok: boolean }>;
  },
};